import { Book1, type Icon, Messages1, ProfileCircle } from 'iconsax-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router';
import { useOnClickOutside } from 'usehooks-ts';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import Badge from '@/components/common/badge';
import AppDownloadPrompt from '@/components/layouts/page-layout/app-download-prompt';
import AppHeaderMenu from '@/components/layouts/page-layout/app-header-menu';
import SessionHeaderMenu from '@/components/sessions/session-list/session-header-menu';
import useFriendRequestCount from '@/hooks/contacts/use-friend-request-count';
import { useSessionUnreadCount } from '@/hooks/sessions/use-conversation-sessions';
import useUserPlatform from '@/hooks/use-user-platform';
import useUserInfo from '@/hooks/users/use-user-info';
import ROUTES from '@/routes';
import { type PanelType, setPanel, useLayoutStore } from '@/stores/layout-status';
import { useMessageStore } from '@/stores/message';
import cn from '@/utils/merge-class-names';

interface NavItemType {
  key: string;
  label: string;
  icon: Icon;
  count?: number;
  className?: string;
  show?: boolean | 'label' | 'icon';
}

export default function BottomBar() {
  const { t } = useTranslation();
  const detailRef = useRef<HTMLDetailsElement>(null);
  const { isDesktop, mode, isEmbedded } = useUserPlatform();
  const { data: userInfo } = useUserInfo();
  const { friendRequestCount } = useFriendRequestCount();
  const { unreadCount } = useSessionUnreadCount(true);
  const { panel: currentPanel } = useLayoutStore();

  const navItems: Array<NavItemType & { panel?: PanelType; onClick?: () => void }> = [
    {
      key: ROUTES.CONTACT.$path({ relative: true }),
      label: 'nav.contact',
      icon: Book1,
      show: mode === 'embedded' ? 'label' : true,
      count: friendRequestCount,
      panel: 'contact',
      onClick: () => setPanel('contact'),
    },
    {
      key: ROUTES.MESSAGES.$path({ relative: true }),
      label: 'nav.messages',
      icon: Messages1,
      show: mode === 'embedded' ? 'label' : true,
      count: unreadCount,
      panel: 'message',
      onClick: () => setPanel('message'),
    },
    {
      key: ROUTES.MY.$path({ relative: true }),
      label: 'nav.my',
      icon: ProfileCircle,
      show: !isDesktop && mode !== 'embedded',
    },
  ];

  useOnClickOutside(detailRef, () => detailRef.current?.removeAttribute('open'));
  return (
    <div className='fixed desktop:absolute inset-x-0 desktop:top-0 desktop:right-auto desktop:bg-primary desktop:flex-col bottom-0 z-50 flex items-center desktop:justify-start justify-evenly bg-neutral-0 p-2 desktop:gap-8 desktop:px-5 desktop:py-6 shadow shadow-black/5 pb-safe-or-2 mode-embedded:top-0 mode-embedded:pt-safe-offset-0 mode-embedded:bottom-auto mode-embedded:justify-end mode-embedded:flex-row-reverse mode-embedded:shadow mode-embedded:bg-primary mode-embedded:pb-0 layout-mini:bg-neutral-0 layout-compact:bg-neutral-0'>
      {isDesktop && userInfo && (
        <Link to={ROUTES.MY.$path({ relative: true })}>
          <AvatarWithStatus
            imageUrl={userInfo.HeadImg}
            label={userInfo.NickName}
            className='size-12'
            size={[48, 48]}
          />
        </Link>
      )}

      {navItems
        .filter(({ show = true }) => show)
        .map(({ key, label, count, show = true, icon: Icon, className, panel, onClick }) => (
          <NavLink
            key={key}
            replace={!isEmbedded}
            state={isEmbedded ? { from: 'comm' } : null}
            to={`/${key}`}
            onClick={() => {
              useMessageStore.getState().resetKey();
              onClick?.();
            }}
            className={cn(
              'flex basis-15vw desktop:basis-14 desktop:size-14 desktop:aspect-square flex-col flex-center mode-embedded:grow-0 mode-embedded:me-4',
              className,
            )}>
            {({ isActive }) => {
              const isHighlighted = isDesktop ? panel === currentPanel : isActive;
              return (
                <div
                  className={cn(
                    'flex indicator h-full mode-embedded:py-4 layout-compact:py-2 layout-mini:py-2 mode-embedded:after:content-dummy mode-embedded:after:absolute mode-embedded:after:inset-x-2 mode-embedded:after:bottom-0.5 mode-embedded:after:h-1 mode-embedded:after:rounded-full transition-colors flex-col flex-center desktop:h-auto',
                    isHighlighted &&
                      'mode-embedded:after:bg-primary-content layout-mini:after:bg-primary layout-compact:after:bg-primary',
                  )}>
                  <Icon
                    className={cn(
                      'size-8 p-0.5 mode-embedded:text-primary-content layout-mini:text-primary layout-compact:text-primary mode-embedded:size-6 mode-embedded:p-0',
                      { hidden: show === 'label' || !show },
                      isHighlighted
                        ? 'text-primary mode-embedded:text-primary-content layout-mini:text-primary layout-compact:text-primary desktop:text-neutral-0'
                        : 'theme-kbar:desktop:text-red-200 theme-ryuryu:desktop:text-blue-200 theme-wangyou:desktop:text-teal-200 theme-threetwoone:desktop:text-purple-200 theme-eightk:desktop:text-violet-200 theme-bonglan:desktop:text-red-200 text-body',
                    )}
                    variant={isHighlighted ? 'Bold' : 'Linear'}
                  />

                  {count ? (
                    <Badge className='indicator-item top-px mode-embedded:top-[15%] -right-1/4'>
                      {count !== Number.POSITIVE_INFINITY ? (count > 99 ? '99+' : count) : ''}
                    </Badge>
                  ) : null}

                  <span
                    className={cn(
                      'text-xs mode-embedded:text-base mode-embedded:text-primary-content layout-mini:text-secondary layout-compact:text-secondary',
                      { hidden: show === 'icon' || !show },
                      isHighlighted
                        ? 'text-primary mode-embedded:font-bold mode-embedded:text-primary-content desktop:text-neutral-0 layout-mini:text-primary layout-compact:text-primary'
                        : 'text-body theme-kbar:desktop:text-red-200 theme-ryuryu:desktop:text-blue-200 theme-wangyou:desktop:text-teal-200 theme-threetwoone:desktop:text-purple-200 theme-eightk:desktop:text-violet-200 theme-bonglan:desktop:text-red-200',
                    )}>
                    {t(label)}
                  </span>
                </div>
              );
            }}
          </NavLink>
        ))}

      {isEmbedded && (
        <>
          {unreadCount > 0 && <SessionHeaderMenu className='-order-2 me-2' />}

          <AppHeaderMenu className='mode-embedded:-order-3' />
        </>
      )}

      <div className='flex-1 desktop:block hidden mode-embedded:block mode-embedded:-order-2' />

      <AppDownloadPrompt />
    </div>
  );
}
